// Last-known shop state (egg balance + owned item ids) for the signed-in
// player, keyed to their user id. A /shop reload paints the balance chip and
// the owned cards from the first frame instead of after two RLS round-trips;
// the fresh fetches then correct anything stale. Same optimism as the nav's
// signed-in hint, and like it this is plain display data, nothing sensitive.
import type { OwnedItems } from "./store";

const KEY = "ar.shopCache";

interface ShopCacheRaw {
  uid: string;
  balance: number | null;
  owned: Record<string, string[]>;
}

function readRaw(): ShopCacheRaw | null {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}

// The cached snapshot; pass a uid to demand it belongs to that account.
export function readShopCache(uid?: string | null): { uid: string; balance: number | null; owned: OwnedItems } | null {
  const raw = readRaw();
  if (!raw || !raw.uid || (uid && raw.uid !== uid)) return null;
  const owned: OwnedItems = {};
  for (const [kind, ids] of Object.entries(raw.owned || {})) owned[kind] = new Set(ids);
  return { uid: raw.uid, balance: raw.balance ?? null, owned };
}

function write(uid: string, patch: Partial<Omit<ShopCacheRaw, "uid">>): void {
  try {
    const cur = readRaw();
    const base: ShopCacheRaw = cur && cur.uid === uid ? cur : { uid, balance: null, owned: {} };
    localStorage.setItem(KEY, JSON.stringify({ ...base, ...patch, uid }));
  } catch {
    /* storage blocked; the page just loads the slow way next time */
  }
}

export function cacheShopBalance(uid: string, balance: number | null): void {
  write(uid, { balance });
}

export function cacheShopOwned(uid: string, owned: OwnedItems | null): void {
  if (!owned) return;
  const flat: Record<string, string[]> = {};
  for (const [kind, ids] of Object.entries(owned)) flat[kind] = [...ids];
  write(uid, { owned: flat });
  // (a won item may have just arrived: the navbar's Store count looks again)
  try { window.dispatchEvent(new Event("ar:unlocks")); } catch { /* no window */ }
}

export function clearShopCache(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
